import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { User } from 'app/core/user/user.model';
import { UserService } from 'app/core/user/user.service';
import { cloneDeep } from 'lodash';
import { ApltFile } from './file-manager.models';
import { IApltFile, IApltFileItems, IFileUploadRequest } from './file-manager.types';

/**
 * File manager service with Angular signals
 * Complete updated version with download and viewer capabilities
 * Handles all file operations for the ApltFile system
 */
@Injectable({ providedIn: 'root' })
export class ApltFileService {
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);

    // Main state with signals
    private _allFiles = signal<ApltFile[]>([]);
    private _currentFile = signal<ApltFile | null>(null);
    private _currentFolderId = signal<string | null>(null);
    private _isLoading = signal<boolean>(false);
    private _error = signal<string | null>(null);

    // Computed signals for views
    public readonly files = this._allFiles.asReadonly();
    public readonly currentFile = this._currentFile.asReadonly();
    public readonly isLoading = this._isLoading.asReadonly();
    public readonly error = this._error.asReadonly();
    public readonly currentFolderId = this._currentFolderId.asReadonly();

    // Computed signal for current view items
    public readonly items = computed<IApltFileItems>(() => {
        const allFiles = this._allFiles();
        const folderId = this._currentFolderId();
        
        console.log('Computing items for folder:', folderId, 'total files:', allFiles.length);
        
        // Filter items by parent folder
        const items = allFiles.filter(item => item.folderId === folderId);
        
        // Separate folders and files
        const folders = items.filter(item => item.type === 'folder');
        const filesOnly = items.filter(item => item.type === 'file');

        // Sort alphabetically
        folders.sort((a, b) => a.name.localeCompare(b.name));
        filesOnly.sort((a, b) => a.name.localeCompare(b.name));

        // Calculate breadcrumb path
        const path = this.calculatePath(folderId, allFiles);

        console.log('Computed items:', { folders: folders.length, files: filesOnly.length });

        return {
            folders,
            files: filesOnly,
            path
        };
    });

    constructor() {
        // Load files on service initialization
        this.loadAllFiles();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public API
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forces a reload of all files from backend
     */
    async refreshFiles(): Promise<void> {
        await this.loadAllFiles();
    }

    /**
     * Sets the current folder context for filtering items
     */
    setCurrentFolder(folderId: string | null): void {
        console.log('Setting current folder to:', folderId);
        this._currentFolderId.set(folderId);
        // Clear current file when changing folder
        this._currentFile.set(null);
    }

    /**
     * Sets the current file for details view
     */
    setCurrentFile(file: ApltFile | null): void {
        console.log('Setting current file to:', file?.name || 'null');
        this._currentFile.set(file);
    }

    /**
     * Gets a specific file by ID and sets it as current
     */
    async getFileById(id: string): Promise<ApltFile | null> {
        console.log('Getting file by ID:', id);
        const files = this._allFiles();
        let found = files.find(file => file.id === id || file.entityId === id) ?? null;

        if (found) {
            this._currentFile.set(cloneDeep(found));
            console.log('File found in cache and set as current:', found.name);
            return found;
        }

        // File not in cache, refresh all files from backend (GET /api/apltfiles)
        console.log('File not in cache, refreshing all files from backend...');
        try {
            await this.loadAllFiles();
            const updatedFiles = this._allFiles();
            found = updatedFiles.find(file => file.id === id || file.entityId === id) ?? null;

            if (found) {
                this._currentFile.set(cloneDeep(found));
                console.log('File found after refresh and set as current:', found.name);
                return found;
            }
        } catch (error) {
            console.error('Error refreshing files from backend:', error);
        }

        console.warn('File not found with ID:', id);
        this._currentFile.set(null);
        return null;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Download Operations
    // -----------------------------------------------------------------------------------------------------

    /**
     * Downloads a file and triggers browser/native download - UPDATED FOR MOBILE
     * Properly handles Content-Disposition header and uses native Capacitor for mobile
     */
    async downloadFile(fileId: string, fileName?: string): Promise<void> {
        console.log('Downloading file ID:', fileId, 'with name:', fileName);
        this._isLoading.set(true);
        this._error.set(null);

        try {
            // Make HTTP request to download endpoint with full response observation
            const response = await this._httpClient
                .get(`aplt_backend/api/apltfiles/files/${fileId}/download`, {
                    responseType: 'blob',
                    observe: 'response'
                })
                .toPromise();

            if (!response || !response.body) {
                throw new Error('No file content received from server');
            }

            const blob = response.body;
            let downloadFileName = fileName;

            // Try to extract filename from Content-Disposition header if not provided
            if (!downloadFileName) {
                const contentDisposition = response.headers.get('Content-Disposition');
                console.log('Content-Disposition header:', contentDisposition);
                console.log('All response headers:', response.headers.keys());
                if (contentDisposition) {
                    // Parse Content-Disposition header: attachment; filename="example.pdf" or attachment;filename="example.pdf"
                    const filenameMatch = contentDisposition.match(/filename\s*=\s*(["']?)([^"';]+)\1/);
                    if (filenameMatch && filenameMatch[2]) {
                        downloadFileName = filenameMatch[2];
                    }

                    // Alternative parsing for filename*=UTF-8''encoded%20name
                    if (!downloadFileName) {
                        const filenameStarMatch = contentDisposition.match(/filename\*=(?:UTF-8'')?([^;\n]*)/);
                        if (filenameStarMatch && filenameStarMatch[1]) {
                            downloadFileName = decodeURIComponent(filenameStarMatch[1]);
                        }
                    }
                }
            }

            // Try to get Content-Type for file extension inference if still no filename
            if (!downloadFileName) {
                const contentType = response.headers.get('Content-Type');
                console.log('Content-Type:', contentType);
                console.log('Blob size:', blob.size);
                console.log('Blob type:', blob.type);

                // Try to detect file type from blob content
                const detectedExtension = await this.detectFileTypeFromBlob(blob);

                if (detectedExtension) {
                    downloadFileName = `Patient_File${detectedExtension}`;
                } else if (contentType && contentType !== 'application/octet-stream') {
                    const extension = this.getExtensionFromMimeType(contentType);
                    downloadFileName = `Patient_File${extension}`;
                } else {
                    downloadFileName = `Patient_File`;
                }
            }

            console.log('Final download filename:', downloadFileName);


            // Use different download methods for web vs mobile
            if (Capacitor.isNativePlatform()) {
                // Mobile: Use Capacitor Filesystem
                await this.downloadFileNative(blob, downloadFileName);
            } else {
                // Web: Use traditional blob download
                await this.downloadFileWeb(blob, downloadFileName);
            }

            console.log('File download completed successfully:', downloadFileName);
        } catch (error) {
            console.error('Error downloading file:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to download file');
            throw error;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Downloads file using web blob method
     */
    private async downloadFileWeb(blob: Blob, fileName: string): Promise<void> {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;

        // Append to body temporarily for download trigger
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    /**
     * Downloads file using Capacitor native filesystem
     */
    private async downloadFileNative(blob: Blob, fileName: string): Promise<void> {
        try {
            // Convert blob to base64
            const base64 = await this.blobToBase64(blob);
            // Remove data URL prefix
            const base64Data = base64.split(',')[1];

            // Write file to Documents directory (accessible to user)
            const result = await Filesystem.writeFile({
                path: fileName,
                data: base64Data,
                directory: Directory.Documents,
                recursive: true
            });

            console.log('File saved to:', result.uri);

            // Show success message
            alert(`Fichier téléchargé avec succès: ${fileName}\nEmplacement: Documents/${fileName}`);
        } catch (error) {
            console.error('Error saving file natively:', error);
            throw new Error('Impossible de sauvegarder le fichier sur l\'appareil');
        }
    }

    /**
     * Converts blob to base64 string
     */
    private blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    /**
     * Downloads a file and returns the blob for further processing - CORRECTED
     */
    async downloadFileBlob(fileId: string): Promise<Blob> {
        this._isLoading.set(true);
        this._error.set(null);

        try {
            const response = await this._httpClient
                .get(`aplt_backend/api/apltfiles/files/${fileId}/download`, {
                    responseType: 'blob'
                })
                .toPromise();

            if (!response) {
                throw new Error('No file content received');
            }

            console.log('File blob downloaded successfully, size:', response.size);
            return response;
        } catch (error) {
            console.error('Error downloading file blob:', error);
            this._error.set(error instanceof Error ? error.message : 'Echec de téléchargement du fichier');
            throw error;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Gets file content as data URL for preview
     */
    async getFileDataUrl(fileId: string): Promise<string> {
        console.log('Getting file data URL for ID:', fileId);

        try {
            const blob = await this.downloadFileBlob(fileId);
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = () => reject(new Error('Echec de lecture du fichier'));
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error('Error getting file data URL:', error);
            throw error;
        }
    }

    /**
     * Gets file metadata from download endpoint headers
     * Downloads the full file to get accurate metadata and blob for preview
     * Useful for users who don't have access to GET /api/apltfiles
     */
    async getFileMetadataFromHeaders(fileId: string): Promise<{ fileName: string; size: number; blob: Blob } | null> {
        try {
            console.log('Downloading file to get metadata for ID:', fileId);

            // Download the full file (CORS blocks headers anyway)
            const response = await this._httpClient
                .get(`aplt_backend/api/apltfiles/files/${fileId}/download`, {
                    responseType: 'blob',
                    observe: 'response'
                })
                .toPromise();

            if (!response || !response.body) {
                return null;
            }

            const blob = response.body;
            const size = blob.size;

            // Try to extract filename from headers (may not work due to CORS)
            let fileName = `file-${fileId.substring(0, 8)}`;
            const contentDisposition = response.headers.get('Content-Disposition') || response.headers.get('content-disposition');
            const customFilename = response.headers.get('filename');

            if (customFilename) {
                fileName = customFilename;
            } else if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename\s*=\s*(["']?)([^"';]+)\1/);
                if (filenameMatch && filenameMatch[2]) {
                    fileName = filenameMatch[2];
                }
            }

            console.log('File metadata extracted:', { fileName, size, blobType: blob.type });
            return { fileName, size, blob };
        } catch (error) {
            console.error('Error getting file metadata from headers:', error);
            return null;
        }
    }

    /**
     * Gets file content as text (for text files)
     */
    async getFileText(fileId: string): Promise<string> {
        console.log('Getting file text for ID:', fileId);
        
        try {
            const blob = await this.downloadFileBlob(fileId);
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = () => reject(new Error('Echec de lecture du fichier'));
                reader.readAsText(blob);
            });
        } catch (error) {
            console.error('Error getting file text:', error);
            throw error;
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ File Type Detection
    // -----------------------------------------------------------------------------------------------------

    /**
     * Checks if a file type can be previewed
     */
    canPreviewFile(fileName: string): boolean {
        const extension = this.getFileExtension(fileName);
        const previewableTypes = [
            'pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp',
            'txt', 'json', 'xml', 'csv', 'log', 'md',
            'mp4', 'webm', 'ogg', 'mp3', 'wav'
        ];
        return previewableTypes.includes(extension);
    }

    /**
     * Gets the file type category for preview handling
     */
    getFileType(fileName: string): 'image' | 'pdf' | 'text' | 'video' | 'audio' | 'unknown' {
        const extension = this.getFileExtension(fileName);
        
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(extension)) {
            return 'image';
        }
        if (extension === 'pdf') {
            return 'pdf';
        }
        if (['txt', 'json', 'xml', 'csv', 'log', 'md', 'js', 'ts', 'html', 'css'].includes(extension)) {
            return 'text';
        }
        if (['mp4', 'webm', 'ogg', 'avi', 'mov'].includes(extension)) {
            return 'video';
        }
        if (['mp3', 'wav', 'ogg', 'aac', 'flac'].includes(extension)) {
            return 'audio';
        }
        return 'unknown';
    }

    /**
     * Gets file extension from filename
     */
    getFileExtension(fileName: string): string {
        return fileName.split('.').pop()?.toLowerCase() || '';
    }

    /**
     * Detect file type from blob magic bytes
     */
    private async detectFileTypeFromBlob(blob: Blob): Promise<string> {
        try {
            // Read first 8 bytes to detect file type
            const arrayBuffer = await blob.slice(0, 8).arrayBuffer();
            const bytes = new Uint8Array(arrayBuffer);

            // PNG signature: 89 50 4E 47 0D 0A 1A 0A
            if (bytes.length >= 8 &&
                bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47 &&
                bytes[4] === 0x0D && bytes[5] === 0x0A && bytes[6] === 0x1A && bytes[7] === 0x0A) {
                return '.png';
            }

            // JPEG signature: FF D8 FF
            if (bytes.length >= 3 && bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
                return '.jpg';
            }

            // PDF signature: 25 50 44 46 (%PDF)
            if (bytes.length >= 4 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
                return '.pdf';
            }

            return '';
        } catch (error) {
            console.warn('Error detecting file type:', error);
            return '';
        }
    }

    /**
     * Get file extension from MIME type
     */
    private getExtensionFromMimeType(mimeType: string): string {
        const mimeToExtension: { [key: string]: string } = {
            'application/pdf': '.pdf',
            'image/jpeg': '.jpg',
            'image/png': '.png',
            'image/gif': '.gif',
            'image/bmp': '.bmp',
            'image/svg+xml': '.svg',
            'image/webp': '.webp',
            'text/plain': '.txt',
            'text/csv': '.csv',
            'application/json': '.json',
            'application/xml': '.xml',
            'text/xml': '.xml',
            'application/msword': '.doc',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
            'application/vnd.ms-excel': '.xls',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
            'application/vnd.ms-powerpoint': '.ppt',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
            'video/mp4': '.mp4',
            'video/webm': '.webm',
            'video/ogg': '.ogg',
            'audio/mpeg': '.mp3',
            'audio/wav': '.wav',
            'audio/ogg': '.ogg',
            'application/zip': '.zip',
            'application/x-rar-compressed': '.rar',
            'application/x-7z-compressed': '.7z',
            'application/dicom': '.dcm'
        };

        return mimeToExtension[mimeType.toLowerCase()] || '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ File Management Operations
    // -----------------------------------------------------------------------------------------------------

    /**
     * Creates a new folder
     */
    async createFolder(folderData: IApltFile): Promise<ApltFile> {
        console.log('Creating folder:', folderData.name, 'in folder:', folderData.folderId);
        this._isLoading.set(true);
        this._error.set(null);

        try {
            const currentUser = this.getCurrentUser();
            const backendFolderData = {
                ...folderData,
                owner: currentUser?.id,
                newEntity: true
            };

            const response = await this._httpClient
                .post<ApltFile>('aplt_backend/api/apltfiles/folders', backendFolderData)
                .toPromise();

            // Reload all files to get the updated list including the new folder
            await this.loadAllFiles();

            // Find the created folder in the updated list
            const allFiles = this._allFiles();
            const createdFolder = allFiles
                .filter(f => f.type === 'folder' && f.name === folderData.name && f.folderId === folderData.folderId)
                .sort((a, b) => new Date(b.zcreationTimestamp || 0).getTime() - new Date(a.zcreationTimestamp || 0).getTime())[0];

            if (!createdFolder) {
                throw new Error('Folder not found after creation');
            }

            console.log('Folder created successfully:', createdFolder.name);
            return createdFolder;
        } catch (error) {
            console.error('Error creating folder:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to create folder');
            throw error;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Uploads a file with metadata
     */
    async uploadFile(uploadRequest: IFileUploadRequest): Promise<ApltFile> {
        console.log('Uploading file:', uploadRequest.file.name, 'to folder:', uploadRequest.metadata.folderId);
        this._isLoading.set(true);
        this._error.set(null);

        try {
            const currentUser = this.getCurrentUser();
            const formData = new FormData();
            formData.append('file', uploadRequest.file);

            const metadata = {
                ...uploadRequest.metadata,
                owner: currentUser?.id,
                newEntity: true
            };

            formData.append('metadata', JSON.stringify(metadata));

            const uploadedFile = await this._httpClient
                .post<ApltFile>('aplt_backend/api/apltfiles/files', formData)
                .toPromise();

            console.log('File uploaded, reloading all files...');
            // Reload all files to get the updated list including the new file
            await this.loadAllFiles();

            // Find the uploaded file in the updated list to ensure it's properly placed
            const allFiles = this._allFiles();
            const foundFile = allFiles.find(f => 
                f.type === 'file' && 
                f.name === uploadRequest.file.name && 
                f.folderId === uploadRequest.metadata.folderId
            );

            if (foundFile) {
                console.log('File upload confirmed in file list:', foundFile.name);
                return foundFile;
            } else {
                console.warn('Uploaded file not found in refreshed list');
                return new ApltFile(uploadedFile!);
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            this._error.set(error instanceof Error ? error.message : 'Echec d\'importation du fichier');
            throw error;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Updates an existing item (not implemented in backend)
     */
    async updateItem(id: string, updates: Partial<IApltFile>): Promise<void> {
        throw new Error('Update functionality not available - no backend endpoint');
    }

    /**
     * Deletes a folder and optionally its contents
     */
    async deleteFolder(id: string, recursive: boolean = false): Promise<boolean> {
        console.log('Deleting folder:', id, 'recursive:', recursive);
        this._isLoading.set(true);
        this._error.set(null);

        try {
            // Clear current file if it's being deleted
            if (this._currentFile()?.id === id) {
                this._currentFile.set(null);
            }

            await this._httpClient
                .delete(`aplt_backend/api/apltfiles/folders/${id}`, { 
                    params: { recursive: recursive.toString() } 
                })
                .toPromise();

            console.log('Folder deletion completed successfully');

            // Reload all files from backend to ensure consistency
            console.log('Reloading files after folder deletion...');
            await this.loadAllFiles();

            return true;
        } catch (error) {
            console.error('Error deleting folder:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to delete folder');
            throw error;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Deletes a specific file
     */
    async deleteFile(id: string): Promise<boolean> {
        console.log('Deleting file:', id);
        this._isLoading.set(true);
        this._error.set(null);

        try {
            // Clear current file if it's being deleted
            if (this._currentFile()?.id === id) {
                this._currentFile.set(null);
            }

            await this._httpClient
                .delete(`aplt_backend/api/apltfiles/files/${id}`)
                .toPromise();

            console.log('File deletion completed successfully');

            // Reload all files from backend to ensure consistency
            console.log('Reloading files after file deletion...');
            await this.loadAllFiles();

            return true;
        } catch (error) {
            console.error('Error deleting file:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to delete file');
            throw error;
        } finally {
            this._isLoading.set(false);
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private Methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Loads all files from backend
     */
    private async loadAllFiles(): Promise<void> {
        this._isLoading.set(true);
        this._error.set(null);

        try {
            console.log('Loading files from backend...');
            const response = await this._httpClient
                .get<ApltFile[]>('aplt_backend/api/apltfiles')
                .toPromise();

            const files = response?.map(item => new ApltFile(item)) || [];
            console.log('Files loaded:', files.length);
            this._allFiles.set(files);
        } catch (error) {
            console.error('Error loading files:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to load files');
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Gets current user from UserService
     */
    private getCurrentUser(): User | null {
        return this._userService.user();
    }

    /**
     * Calculates breadcrumb path from root to specified folder
     */
    private calculatePath(folderId: string | null, allFiles: ApltFile[]): ApltFile[] {
        if (!folderId) return [];

        const path: ApltFile[] = [];
        let currentFolder = allFiles.find(file => file.id === folderId);

        while (currentFolder) {
            path.unshift(currentFolder);
            currentFolder = currentFolder.folderId 
                ? allFiles.find(file => file.id === currentFolder!.folderId) 
                : undefined;
        }

        return path;
    }

    /**
     * Checks if a file/folder is a child of the specified folder
     */
    private isChildOfFolder(item: ApltFile, folderId: string, allFiles: ApltFile[]): boolean {
        let currentItem = item;

        while (currentItem.folderId) {
            if (currentItem.folderId === folderId) {
                return true;
            }
            currentItem = allFiles.find(f => f.id === currentItem.folderId)!;
            if (!currentItem) break;
        }

        return false;
    }
}